import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, ChevronDown, Printer, Check, Award, Smartphone } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { Layout } from "@/components/Layout";
import { ModuleIntro, ModuleBanner } from "@/components/ModuleIntro";
import { LanguageToggle } from "@/components/LanguageToggle";
import { usePatientMode } from "@/lib/patientMode";
import { downloadElementAsPdf } from "@/lib/pdf";
import { toast } from "sonner";
import { PROG_T, PROGRAMME_KEY, PROG_DAYS } from "@/data/programme";

const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

const freshW1Day = () => ({ positif: {}, negatif: {}, victoire: {}, energie: 5, charge: 5, autrePositif: "", autreNegatif: "", autreVictoire: "", note: "" });
const freshW2Day = () => ({ facilitant: {}, empechant: {}, bloquant: {}, autreF: "", autreE: "", autreB: "", note: "" });
const freshState = () => ({
  w1: { days: Array.from({ length: PROG_DAYS }, freshW1Day), bilan: { patterns: "", ajust: ["", "", "", ""] } },
  w2: { days: Array.from({ length: PROG_DAYS }, freshW2Day) },
  w3: { priorites: ["", "", ""] },
});

const loadState = () => {
  try {
    const raw = localStorage.getItem(PROGRAMME_KEY);
    if (raw) return { ...freshState(), ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return freshState();
};

const selectedLabels = (values, options, autre) => {
  const arr = options.filter((_, i) => values[i]).map((o) => o);
  if (autre) arr.push(autre);
  return arr;
};

const w1DayHasData = (d) => Object.keys(d.positif).some((k) => d.positif[k]) || Object.keys(d.negatif).some((k) => d.negatif[k]) || Object.keys(d.victoire).some((k) => d.victoire[k]) || d.autrePositif || d.autreNegatif || d.autreVictoire || d.note;
const w2DayHasData = (d) => Object.keys(d.facilitant).some((k) => d.facilitant[k]) || Object.keys(d.empechant).some((k) => d.empechant[k]) || Object.keys(d.bloquant).some((k) => d.bloquant[k]) || d.autreF || d.autreE || d.autreB || d.note;
const topFactors = (days, cat, options) => {
  const counts = {};
  days.forEach((d) => Object.keys(d[cat] || {}).forEach((k) => { if (d[cat][k]) counts[k] = (counts[k] || 0) + 1; }));
  return Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([i, n]) => `${options[i]} (${n})`);
};

// Détermine où le patient s'est arrêté : propose le prochain jour vide à remplir.
const firstEmpty = (days, hasData) => { for (let i = 0; i < days.length; i++) if (!hasData(days[i])) return i; return -1; };
const lastFilled = (days, hasData) => days.reduce((m, d, i) => (hasData(d) ? i : m), -1);
const computeResume = (state) => {
  if (state.w3.priorites.some((p) => p)) return { phase: "w3" };
  if (lastFilled(state.w2.days, w2DayHasData) >= 0) {
    const e = firstEmpty(state.w2.days, w2DayHasData);
    return e === -1 ? { phase: "w3" } : { phase: "w2", day: e };
  }
  if (state.w1.bilan.patterns || state.w1.bilan.ajust.some((a) => a)) return { phase: "w2", day: 0 };
  if (lastFilled(state.w1.days, w1DayHasData) >= 0) {
    const e = firstEmpty(state.w1.days, w1DayHasData);
    return e === -1 ? { phase: "bilan1" } : { phase: "w1", day: e };
  }
  return null;
};

const Primary = ({ children, ...p }) => (
  <button {...p} className="inline-flex items-center gap-1.5 px-7 py-3 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all disabled:opacity-60">{children}</button>
);
const Ghost = ({ children, ...p }) => (
  <button {...p} className="inline-flex items-center gap-1.5 px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-colors">{children}</button>
);

const Checklist = ({ title, hint, options, values, onToggle, autre, onAutre, otherPh, testid }) => (
  <div className="rounded-2xl bg-white/60 border border-[var(--border)] p-5" data-testid={testid}>
    <h3 className="font-semibold text-[var(--ink)] mb-1">{title}</h3>
    {hint && <p className="text-sm text-[var(--ink-soft)] mb-3">{hint}</p>}
    <div className="flex flex-wrap gap-2 mb-3">
      {options.map((opt, i) => (
        <button key={i} onClick={() => onToggle(i)} data-testid={`${testid}-opt-${i}`}
          className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full border transition-all ${values[i] ? "bg-[var(--gold)] text-white border-[var(--gold)]" : "border-[var(--border)] text-[var(--ink)] hover:border-[var(--gold)]"}`}>
          {values[i] && <Check size={13} />} {opt}
        </button>
      ))}
    </div>
    <input value={autre} onChange={(e) => onAutre(e.target.value)} data-testid={`${testid}-autre`} placeholder={otherPh}
      className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-2.5 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)]" />
  </div>
);

const Expandable = ({ title, children, testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl bg-white/60 border border-[var(--border)] overflow-hidden" data-testid={testid}>
      <button onClick={() => setOpen((o) => !o)} data-testid={`${testid}-toggle`} className="w-full flex items-center justify-between gap-3 p-4 text-left">
        <span className="font-serif-title text-lg text-[var(--gold)]">{title}</span>
        <ChevronDown size={18} className={`text-[var(--gold)] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-4 pb-4 text-sm text-[var(--ink)] leading-relaxed space-y-3">{children}</div>}
    </div>
  );
};

const WeekBar = ({ current }) => (
  <div className="flex items-center justify-center gap-2 mb-6">
    {[1, 2, 3].map((w) => (
      <div key={w} className={`h-1.5 rounded-full transition-all ${w === current ? "w-10 bg-[var(--gold)]" : w < current ? "w-6 bg-[var(--gold-light)]" : "w-6 bg-[var(--beige)]"}`} />
    ))}
  </div>
);

const DayNav = ({ day, onPick, testid }) => (
  <div className="flex flex-wrap items-center justify-center gap-2 mb-6" data-testid={testid}>
    {Array.from({ length: PROG_DAYS }, (_, i) => (
      <button key={i} onClick={() => onPick(i)} data-testid={`${testid}-${i}`}
        className={`h-9 w-9 rounded-full text-sm font-medium border transition-all ${i === day ? "bg-[var(--gold)] text-white border-[var(--gold)]" : "border-[var(--border)] text-[var(--ink)] hover:border-[var(--gold)]"}`}>
        {i + 1}
      </button>
    ))}
  </div>
);

export default function ProgrammeModule({ initialLang }) {
  const { patientMode } = usePatientMode() || {};
  const [lang, setLang] = useState(initialLang || "fr");
  const [phase, setPhase] = useState("welcome");
  const [w1Day, setW1Day] = useState(0);
  const [w2Day, setW2Day] = useState(0);
  const [state, setState] = useState(loadState);
  const [exporting, setExporting] = useState(false);
  const printRef = React.useRef(null);
  const t = PROG_T[lang];

  useEffect(() => {
    try { localStorage.setItem(PROGRAMME_KEY, JSON.stringify(state)); } catch { /* ignore */ }
  }, [state]);

  const daysAllFilled = (days, hasData) => days.length === PROG_DAYS && days.every(hasData);
  const w1Done = daysAllFilled(state.w1.days, w1DayHasData);
  const w2Done = daysAllFilled(state.w2.days, w2DayHasData);
  const w3Done = state.w3.priorites.every((p) => p.trim());
  const prevDone = React.useRef({ w1: w1Done, w2: w2Done, w3: w3Done });
  useEffect(() => {
    const p = prevDone.current;
    if (w1Done && !p.w1) toast.success(t.badgeWeekDone(1));
    if (w2Done && !p.w2) toast.success(t.badgeWeekDone(2));
    if (w3Done && !p.w3) toast.success(t.badgeWeekDone(3));
    prevDone.current = { w1: w1Done, w2: w2Done, w3: w3Done };
  }, [w1Done, w2Done, w3Done]); // eslint-disable-line react-hooks/exhaustive-deps

  const Badges = () => (
    <div>
      <p className="text-xs uppercase tracking-[0.18em] text-[var(--ink-soft)] text-center mb-3">{t.badgesTitle}</p>
      <div className="flex items-center justify-center gap-6" data-testid="prog-badges">
        {[{ n: 1, done: w1Done }, { n: 2, done: w2Done }, { n: 3, done: w3Done }].map((b) => (
          <div key={b.n} className={`flex flex-col items-center gap-1.5 transition-opacity ${b.done ? "" : "opacity-35"}`} data-testid={`prog-badge-${b.n}${b.done ? "-done" : ""}`}>
            <div className={`h-14 w-14 rounded-full flex items-center justify-center border-2 ${b.done ? "bg-[var(--gold)] border-[var(--gold)] text-white shadow-sm" : "border-[var(--border)] text-[var(--ink-soft)]"}`}>
              <Award size={24} />
            </div>
            <span className="text-xs text-[var(--ink-soft)]">{t.weekWord} {b.n}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const WeekDoneBanner = ({ n, testid }) => (
    <div className="rounded-full bg-[var(--gold)] text-white text-sm text-center py-2 px-4 mb-5 flex items-center justify-center gap-2 animate-fade-up" data-testid={testid}>
      <Award size={16} /> {t.badgeWeekDone(n)}
    </div>
  );

  const setW1 = (i, patch) => setState((s) => ({ ...s, w1: { ...s.w1, days: s.w1.days.map((d, j) => (j === i ? { ...d, ...patch } : d)) } }));
  const toggleW1 = (i, cat, k) => setW1(i, { [cat]: { ...state.w1.days[i][cat], [k]: !state.w1.days[i][cat][k] } });
  const setW2 = (i, patch) => setState((s) => ({ ...s, w2: { ...s.w2, days: s.w2.days.map((d, j) => (j === i ? { ...d, ...patch } : d)) } }));
  const toggleW2 = (i, cat, k) => setW2(i, { [cat]: { ...state.w2.days[i][cat], [k]: !state.w2.days[i][cat][k] } });
  const setBilan = (patch) => setState((s) => ({ ...s, w1: { ...s.w1, bilan: { ...s.w1.bilan, ...patch } } }));
  const setAjust = (i, v) => setState((s) => ({ ...s, w1: { ...s.w1, bilan: { ...s.w1.bilan, ajust: s.w1.bilan.ajust.map((a, j) => (j === i ? v : a)) } } }));
  const setPrio = (i, v) => setState((s) => ({ ...s, w3: { priorites: s.w3.priorites.map((p, j) => (j === i ? v : p)) } }));

  const restart = () => { setState(freshState()); setW1Day(0); setW2Day(0); setPhase("welcome"); scrollTop(); };

  const doExport = async () => {
    if (!printRef.current) return;
    setExporting(true);
    try { await downloadElementAsPdf(printRef.current, "programme-3-semaines.pdf"); }
    catch { toast.error(lang === "es" ? "No se pudo generar el PDF." : "Impossible de générer le PDF."); }
    setExporting(false);
  };

  const Header = () => (
    <div className="flex items-center justify-between mb-6 no-print">
      {patientMode ? <span /> : <Link to="/" data-testid="prog-home-link" className="text-sm text-[var(--ink-soft)] hover:text-[var(--gold)] transition-colors">{t.allModules}</Link>}
      <LanguageToggle lang={lang} onChange={setLang} />
    </div>
  );

  // ---------- WELCOME ----------
  if (phase === "welcome") {
    const resume = computeResume(state);
    const resumeLabel = resume ? (
      resume.phase === "w1" ? `${t.weekWord} 1 · ${t.dayWord} ${resume.day + 1} ${t.of} ${PROG_DAYS}`
      : resume.phase === "bilan1" ? t.bilan1Title
      : resume.phase === "w2" ? `${t.weekWord} 2 · ${t.dayWord} ${resume.day + 1} ${t.of} ${PROG_DAYS}`
      : t.w3Title
    ) : "";
    const doResume = () => {
      if (resume.phase === "w1") setW1Day(resume.day);
      if (resume.phase === "w2") setW2Day(resume.day);
      setPhase(resume.phase); scrollTop();
    };
    return (
      <Layout>
        <Header />
        <div className="max-w-2xl mx-auto animate-fade-up">
          <ModuleBanner moduleKey="programme-3-semaines" className="mb-8" />
          <h1 className="font-serif-title text-4xl sm:text-5xl text-[var(--gold)] leading-tight mb-6 text-center" data-testid="prog-hero">{t.welcomeTitle}</h1>
          {resume && (
            <div className="rounded-3xl bg-[var(--sand)] border border-[var(--gold-light)] p-5 mb-6 text-center" data-testid="prog-resume">
              <p className="text-sm text-[var(--ink-soft)] mb-1">{t.resumeTitle}</p>
              <p className="font-serif-title text-2xl text-[var(--gold)] mb-4" data-testid="prog-resume-label">{resumeLabel}</p>
              <Primary onClick={doResume} data-testid="prog-resume-btn">{t.resumeBtn} <ChevronRight size={16} /></Primary>
            </div>
          )}
          {(resume || w1Done || w2Done || w3Done) && <div className="mb-6"><Badges /></div>}
          <p className="text-[var(--ink)] leading-relaxed text-center mb-6">{t.welcomeText}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
            {t.philo.map((p, i) => (
              <div key={i} className="rounded-2xl bg-[var(--sand)] border border-[var(--border)] p-4 text-center text-sm font-medium text-[var(--gold)]">{p}</div>
            ))}
          </div>
          <ModuleIntro moduleKey="programme-3-semaines" lang={lang} className="mb-8" />
          <div className="rounded-3xl bg-white/70 border-2 border-[var(--gold-light)] p-5 mb-8 flex gap-3" data-testid="prog-local-notice">
            <Smartphone size={20} className="text-[var(--gold)] shrink-0 mt-0.5" />
            <div><p className="font-semibold text-[var(--ink)] mb-1">{t.localTitle}</p><p className="text-sm text-[var(--ink-soft)] leading-relaxed">{t.localText}</p></div>
          </div>
          <div className="text-center">
            <Primary onClick={() => { setPhase("avant"); scrollTop(); }} data-testid="prog-btn-start">{resume ? t.startOver : t.start}</Primary>
          </div>
        </div>
      </Layout>
    );
  }

  // ---------- AVANT DE COMMENCER ----------
  if (phase === "avant") {
    return (
      <Layout>
        <Header />
        <div className="max-w-2xl mx-auto animate-fade-up">
          <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-6 text-center">{t.avantTitle}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <div className="rounded-2xl border-2 border-[#B0553B] p-5" data-testid="prog-avant-not">
              <h3 className="font-semibold text-[#B0553B] mb-3">✕ {t.avantNotTitle}</h3>
              <ul className="space-y-1.5">{t.avantNot.map((x, i) => (<li key={i} className="text-sm text-[var(--ink)]">• {x}</li>))}</ul>
            </div>
            <div className="rounded-2xl border-2 border-[#8A9A6D] p-5" data-testid="prog-avant-seek">
              <h3 className="font-semibold text-[#6f7d54] mb-3">✓ {t.avantSeekTitle}</h3>
              <ul className="space-y-1.5">{t.avantSeek.map((x, i) => (<li key={i} className="text-sm text-[var(--ink)]">• {x}</li>))}</ul>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <Ghost onClick={() => { setPhase("welcome"); scrollTop(); }}><ChevronLeft size={16} /> {t.prev}</Ghost>
            <Primary onClick={() => { setPhase("w1"); setW1Day(0); scrollTop(); }} data-testid="prog-avant-next">{t.avantNext} <ChevronRight size={16} /></Primary>
          </div>
        </div>
      </Layout>
    );
  }

  // ---------- SEMAINE 1 ----------
  if (phase === "w1") {
    const d = state.w1.days[w1Day];
    return (
      <Layout>
        <Header />
        <div className="max-w-2xl mx-auto animate-fade-up">
          <WeekBar current={1} />
          <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-2 text-center">{t.w1Title}</h2>
          <p className="text-[var(--ink)] leading-relaxed text-center mb-4">{t.w1Intro}</p>
          <div className="rounded-full bg-[var(--gold)]/10 border border-[var(--gold-light)] text-[var(--gold)] text-sm text-center py-2 px-4 mb-6" data-testid="prog-daily-rule">⏱ {t.dailyRule}</div>
          {w1Done && <WeekDoneBanner n={1} testid="prog-w1-complete" />}
          <DayNav day={w1Day} onPick={(i) => { setW1Day(i); scrollTop(); }} testid="prog-w1-daynav" />
          <p className="text-center text-sm uppercase tracking-[0.18em] text-[var(--ink-soft)] mb-5">{t.dayWord} {w1Day + 1} {t.of} {PROG_DAYS}</p>

          <div className="space-y-4">
            <Checklist title={t.w1PositifTitle} options={t.w1Positif} values={d.positif} onToggle={(k) => toggleW1(w1Day, "positif", k)} autre={d.autrePositif} onAutre={(v) => setW1(w1Day, { autrePositif: v })} otherPh={t.otherPlaceholder} testid="prog-w1-positif" />
            <Checklist title={t.w1NegatifTitle} options={t.w1Negatif} values={d.negatif} onToggle={(k) => toggleW1(w1Day, "negatif", k)} autre={d.autreNegatif} onAutre={(v) => setW1(w1Day, { autreNegatif: v })} otherPh={t.otherPlaceholder} testid="prog-w1-negatif" />
            <div className="rounded-2xl bg-white/60 border border-[var(--border)] p-5" data-testid="prog-w1-energy">
              <h3 className="font-semibold text-[var(--ink)] mb-1">{t.w1EnergyTitle}</h3>
              <p className="text-sm text-[var(--ink-soft)] mb-3">{t.w1EnergyHint} <strong className="text-[var(--gold)]">{d.energie}/10</strong></p>
              <input type="range" min="0" max="10" value={d.energie} onChange={(e) => setW1(w1Day, { energie: Number(e.target.value) })} data-testid="prog-w1-energy-slider" className="w-full accent-[var(--gold)]" />
            </div>
            <div className="rounded-2xl bg-white/60 border border-[var(--border)] p-5" data-testid="prog-w1-charge">
              <h3 className="font-semibold text-[var(--ink)] mb-1">{t.w1ChargeTitle}</h3>
              <p className="text-sm text-[var(--ink-soft)] mb-3">{t.w1ChargeHint} <strong className="text-[var(--gold)]">{d.charge ?? 5}/10</strong></p>
              <input type="range" min="0" max="10" value={d.charge ?? 5} onChange={(e) => setW1(w1Day, { charge: Number(e.target.value) })} data-testid="prog-w1-charge-slider" className="w-full accent-[var(--gold)]" />
            </div>
            <Checklist title={t.w1VictoireTitle} options={t.w1Victoire} values={d.victoire} onToggle={(k) => toggleW1(w1Day, "victoire", k)} autre={d.autreVictoire} onAutre={(v) => setW1(w1Day, { autreVictoire: v })} otherPh={t.otherPlaceholder} testid="prog-w1-victoire" />
            <textarea rows={2} value={d.note} onChange={(e) => setW1(w1Day, { note: e.target.value })} data-testid="prog-w1-note" placeholder={t.dayNotePlaceholder}
              className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-3 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)] resize-y" />
          </div>

          <p className="text-center text-xs text-[var(--ink-soft)] mt-5 no-print">✓ {t.saved} — {t.localTitle.toLowerCase()}</p>
          <div className="flex items-center justify-between mt-4">
            <Ghost onClick={() => { if (w1Day === 0) { setPhase("avant"); } else setW1Day(w1Day - 1); scrollTop(); }} data-testid="prog-w1-prev"><ChevronLeft size={16} /> {t.prev}</Ghost>
            {w1Day < PROG_DAYS - 1 ? (
              <Primary onClick={() => { setW1Day(w1Day + 1); scrollTop(); }} data-testid="prog-w1-next">{t.next} <ChevronRight size={16} /></Primary>
            ) : (
              <Primary onClick={() => { setPhase("bilan1"); scrollTop(); }} data-testid="prog-w1-to-bilan">{t.toBilan} <ChevronRight size={16} /></Primary>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  // ---------- BILAN SEMAINE 1 ----------
  if (phase === "bilan1") {
    return (
      <Layout>
        <Header />
        <div className="max-w-2xl mx-auto animate-fade-up">
          <WeekBar current={1} />
          <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-2 text-center">{t.bilan1Title}</h2>
          <p className="text-[var(--ink)] leading-relaxed text-center mb-6">{t.bilan1Text}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
            {t.bilan1Steps.map((s, i) => (
              <div key={i} className="rounded-2xl bg-[var(--sand)] border border-[var(--border)] p-4">
                <p className="font-semibold text-[var(--gold)] mb-1">{s.t}</p>
                <p className="text-sm text-[var(--ink-soft)]">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-[var(--ink)] mb-1.5">{t.bilan1PatternsLabel}</label>
              <textarea rows={3} value={state.w1.bilan.patterns} onChange={(e) => setBilan({ patterns: e.target.value })} data-testid="prog-bilan-patterns" placeholder={t.bilan1PatternsPh}
                className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-3 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)] resize-y" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--ink)] mb-2">{t.bilan1AjustLabel}</label>
              <div className="space-y-2">
                {state.w1.bilan.ajust.map((a, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="flex items-center justify-center h-7 w-7 rounded-full bg-[var(--gold)] text-white text-sm shrink-0">{i + 1}</span>
                    <input value={a} onChange={(e) => setAjust(i, e.target.value)} data-testid={`prog-bilan-ajust-${i}`} placeholder={`${t.bilan1AjustPh} ${i + 1}`}
                      className="flex-1 rounded-xl border border-[var(--border)] bg-white/80 p-2.5 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)]" />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <p className="text-sm text-[var(--ink-soft)] italic text-center mb-6">{t.bilan1Closing}</p>
          <div className="flex items-center justify-between">
            <Ghost onClick={() => { setPhase("w1"); setW1Day(PROG_DAYS - 1); scrollTop(); }} data-testid="prog-bilan-prev"><ChevronLeft size={16} /> {t.prev}</Ghost>
            <Primary onClick={() => { setPhase("w2"); setW2Day(0); scrollTop(); }} data-testid="prog-to-week2">{t.toWeek2} <ChevronRight size={16} /></Primary>
          </div>
        </div>
      </Layout>
    );
  }

  // ---------- SEMAINE 2 ----------
  if (phase === "w2") {
    const d = state.w2.days[w2Day];
    return (
      <Layout>
        <Header />
        <div className="max-w-2xl mx-auto animate-fade-up">
          <WeekBar current={2} />
          <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-2 text-center">{t.w2Title}</h2>
          <p className="text-[var(--ink)] leading-relaxed text-center mb-6">{t.w2Intro}</p>
          {w2Done && <WeekDoneBanner n={2} testid="prog-w2-complete" />}
          <DayNav day={w2Day} onPick={(i) => { setW2Day(i); scrollTop(); }} testid="prog-w2-daynav" />
          <p className="text-center text-sm uppercase tracking-[0.18em] text-[var(--ink-soft)] mb-5">{t.dayWord} {w2Day + 1} {t.of} {PROG_DAYS}</p>

          <div className="space-y-4">
            <Checklist title={t.w2FacilitantTitle} hint={t.w2FacilitantHint} options={t.w2Facilitant} values={d.facilitant} onToggle={(k) => toggleW2(w2Day, "facilitant", k)} autre={d.autreF} onAutre={(v) => setW2(w2Day, { autreF: v })} otherPh={t.otherPlaceholder} testid="prog-w2-facilitant" />
            <Checklist title={t.w2EmpechantTitle} hint={t.w2EmpechantHint} options={t.w2Empechant} values={d.empechant} onToggle={(k) => toggleW2(w2Day, "empechant", k)} autre={d.autreE} onAutre={(v) => setW2(w2Day, { autreE: v })} otherPh={t.otherPlaceholder} testid="prog-w2-empechant" />
            <Checklist title={t.w2BloquantTitle} hint={t.w2BloquantHint} options={t.w2Bloquant} values={d.bloquant} onToggle={(k) => toggleW2(w2Day, "bloquant", k)} autre={d.autreB} onAutre={(v) => setW2(w2Day, { autreB: v })} otherPh={t.otherPlaceholder} testid="prog-w2-bloquant" />
            <textarea rows={2} value={d.note} onChange={(e) => setW2(w2Day, { note: e.target.value })} data-testid="prog-w2-note" placeholder={t.dayNotePlaceholder}
              className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-3 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)] resize-y" />
          </div>

          <p className="text-center text-xs text-[var(--ink-soft)] mt-5 no-print">✓ {t.saved} — {t.localTitle.toLowerCase()}</p>
          <div className="flex items-center justify-between mt-4">
            <Ghost onClick={() => { if (w2Day === 0) { setPhase("bilan1"); } else setW2Day(w2Day - 1); scrollTop(); }} data-testid="prog-w2-prev"><ChevronLeft size={16} /> {t.prev}</Ghost>
            {w2Day < PROG_DAYS - 1 ? (
              <Primary onClick={() => { setW2Day(w2Day + 1); scrollTop(); }} data-testid="prog-w2-next">{t.next} <ChevronRight size={16} /></Primary>
            ) : (
              <Primary onClick={() => { setPhase("w3"); scrollTop(); }} data-testid="prog-to-week3">{t.toWeek3} <ChevronRight size={16} /></Primary>
            )}
          </div>
        </div>
      </Layout>
    );
  }

  // ---------- SEMAINE 3 ----------
  if (phase === "w3") {
    const c = t.concepts;
    return (
      <Layout>
        <Header />
        <div className="max-w-2xl mx-auto animate-fade-up">
          <WeekBar current={3} />
          <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-2 text-center">{t.w3Title}</h2>
          <p className="text-[var(--ink)] leading-relaxed text-center mb-6">{t.w3Intro}</p>
          {w3Done && <WeekDoneBanner n={3} testid="prog-w3-complete" />}

          <div className="rounded-2xl bg-[var(--sand)] border border-[var(--border)] p-5 mb-6" data-testid="prog-w3-priorites">
            <h3 className="font-semibold text-[var(--ink)] mb-3">{t.w3PrioTitle}</h3>
            <div className="space-y-3">
              {state.w3.priorites.map((p, i) => {
                const lv = t.concepts.prioLevels[i];
                return (
                  <div key={i} className="rounded-xl bg-white/70 border border-[var(--border)] p-3" data-testid={`prog-w3-prio-card-${i}`}>
                    <div className="flex items-start gap-2 mb-2">
                      <span className="flex items-center justify-center h-7 w-7 rounded-full bg-[var(--gold)] text-white text-sm shrink-0">{i + 1}</span>
                      <div>
                        <p className="text-sm font-semibold text-[var(--ink)]">{lv.label}</p>
                        <p className="text-xs text-[var(--ink-soft)]">{lv.desc}</p>
                      </div>
                    </div>
                    <input value={p} onChange={(e) => setPrio(i, e.target.value)} data-testid={`prog-w3-prio-${i}`} placeholder={t.w3PrioPh}
                      className="w-full rounded-xl border border-[var(--border)] bg-white/80 p-2.5 text-sm text-[var(--ink)] focus:outline-none focus:ring-2 focus:ring-[var(--gold-light)]" />
                  </div>
                );
              })}
            </div>
          </div>

          <h3 className="font-serif-title text-xl text-[var(--gold)] mb-1">{t.w3ToolsTitle}</h3>
          <p className="text-sm text-[var(--ink-soft)] mb-4">{t.w3ToolsHint}</p>
          <div className="space-y-3 mb-6">
            <Expandable title={c.motivationTitle} testid="prog-concept-motivation">
              <p className="italic text-[var(--ink-soft)]">{c.motivationPiege}</p>
              <p className="font-semibold text-[var(--ink)]">{c.motivationBuildTitle}</p>
              <ul className="list-disc pl-5 space-y-0.5">{c.motivationBuild.map((x, i) => (<li key={i}>{x}</li>))}</ul>
              <p className="font-semibold text-[var(--ink)]">{c.motivationRefTitle}</p>
              <div className="space-y-1.5">
                {c.motivationRefs.map((r, i) => (
                  <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-1 text-sm">
                    <span className="text-[#B0553B]">{r.neg}</span><span className="hidden sm:inline text-[var(--gold)]">→</span><span className="text-[#6f7d54]">{r.pos}</span>
                  </div>
                ))}
              </div>
              <p className="text-[var(--gold)] font-serif-title text-lg">« {c.motivationMotto} »</p>
            </Expandable>

            <Expandable title={c.chargeTitle} testid="prog-concept-charge">
              <p className="text-[var(--gold)] font-serif-title text-lg">« {c.chargeMotto} »</p>
              <p>{c.chargeExpl}</p>
              <p className="font-semibold text-[var(--ink)]">{c.chargeTransformTitle}</p>
              <p className="text-[#B0553B] line-through">{c.chargeTransformFrom}</p>
              <ol className="list-decimal pl-5 space-y-0.5">{c.chargeTransformSteps.map((x, i) => (<li key={i}>{x}</li>))}</ol>
              <p className="font-semibold text-[var(--ink)]">{c.chargeReduceTitle}</p>
              <div className="space-y-1.5">
                {c.chargeReduce.map((r, i) => (
                  <div key={i} className="flex flex-col sm:flex-row sm:items-center gap-1 text-sm">
                    <span className="text-[#B0553B]">{r.q}</span><span className="hidden sm:inline text-[var(--gold)]">→</span><span className="text-[#6f7d54]">{r.a}</span>
                  </div>
                ))}
              </div>
              <p className="font-semibold text-[var(--ink)]">{c.chargeToolsTitle}</p>
              <div className="flex flex-wrap gap-2">{c.chargeTools.map((x, i) => (<span key={i} className="text-xs px-2.5 py-1 rounded-full bg-[var(--sand)] border border-[var(--border)]">{x}</span>))}</div>
            </Expandable>

            <Expandable title={c.prioTitle} testid="prog-concept-prio">
              <p className="text-[var(--gold)] font-serif-title text-lg">« {c.prioMotto} »</p>
              <p>{c.prioExpl}</p>
              <div className="space-y-2">
                {c.prioLevels.map((lv) => (
                  <div key={lv.n} className="rounded-xl bg-[var(--sand)] border border-[var(--border)] p-3">
                    <p className="font-semibold text-[var(--ink)]">{lv.label}</p>
                    <p className="text-sm text-[var(--ink-soft)]">{lv.desc}</p>
                  </div>
                ))}
              </div>
              <p className="font-semibold text-[var(--ink)]">{c.deadlineTitle}</p>
              <p>{c.deadlineExpl}</p>
              <ul className="space-y-1">{c.deadlineSteps.map((s, i) => (<li key={i} className="text-sm"><strong className="text-[var(--gold)]">{s.day} :</strong> {s.task}</li>))}</ul>
              <p className="text-sm text-[var(--ink-soft)] italic">{c.prioClosing}</p>
            </Expandable>

            <Expandable title={t.boiteTitle} testid="prog-concept-boite">
              <p>{t.boiteIntro}</p>
              <div className="space-y-2">
                {t.boite.map((b, i) => (
                  <div key={i} className="rounded-xl bg-[var(--sand)] border border-[var(--border)] p-3">
                    <p className="font-semibold text-[var(--ink)] mb-1">{b.problem}</p>
                    <div className="flex flex-wrap gap-2">{b.solutions.map((s, j) => (<span key={j} className="text-xs px-2.5 py-1 rounded-full bg-white/70 border border-[var(--border)]">{s}</span>))}</div>
                  </div>
                ))}
              </div>
            </Expandable>
          </div>

          <div className="flex items-center justify-between">
            <Ghost onClick={() => { setPhase("w2"); setW2Day(PROG_DAYS - 1); scrollTop(); }} data-testid="prog-w3-prev"><ChevronLeft size={16} /> {t.prev}</Ghost>
            <Primary onClick={() => { setPhase("done"); scrollTop(); }} data-testid="prog-to-end">{t.toEnd} <ChevronRight size={16} /></Primary>
          </div>
        </div>
      </Layout>
    );
  }

  // ---------- DONE / PDF ----------
  const w1Filled = state.w1.days.map((d, i) => ({ i, d })).filter(({ d }) => Object.keys(d.positif).some((k) => d.positif[k]) || Object.keys(d.negatif).some((k) => d.negatif[k]) || Object.keys(d.victoire).some((k) => d.victoire[k]) || d.autrePositif || d.autreNegatif || d.autreVictoire || d.note);
  const w2Filled = state.w2.days.map((d, i) => ({ i, d })).filter(({ d }) => Object.keys(d.facilitant).some((k) => d.facilitant[k]) || Object.keys(d.empechant).some((k) => d.empechant[k]) || Object.keys(d.bloquant).some((k) => d.bloquant[k]) || d.autreF || d.autreE || d.autreB || d.note);
  const join = (arr) => (arr.length ? arr.join(", ") : t.emptyDay);
  const energyData = state.w1.days.map((d, i) => ({ jour: `J${i + 1}`, energie: d.energie ?? 5, charge: d.charge ?? 5 }));
  const avgE = (energyData.reduce((s, d) => s + d.energie, 0) / PROG_DAYS).toFixed(1);
  const avgC = (energyData.reduce((s, d) => s + d.charge, 0) / PROG_DAYS).toFixed(1);

  return (
    <Layout>
      <div className="max-w-3xl mx-auto animate-fade-up">
        <div className="text-center mb-8 no-print">
          <h2 className="font-serif-title text-4xl text-[var(--gold)] mb-3">{t.finishTitle}</h2>
          <p className="text-[var(--ink-soft)] leading-relaxed">{t.finishText}</p>
        </div>

        <div className="mb-8 no-print"><Badges /></div>
        {w1Done && w2Done && w3Done && (
          <div className="rounded-3xl bg-[var(--gold)] text-white p-7 text-center mb-8 no-print animate-fade-up" data-testid="prog-programme-done">
            <Award size={40} className="mx-auto mb-3" />
            <h3 className="font-serif-title text-3xl mb-1">{t.programmeDoneTitle}</h3>
            <p className="text-sm text-white/90 max-w-md mx-auto">{t.programmeDoneText}</p>
          </div>
        )}

        <div ref={printRef} className="print-container bg-white p-6 sm:p-8 rounded-2xl border border-[var(--border)]">
          <h1 className="font-serif-title text-2xl text-[var(--gold)] mb-4">{t.printTitle}</h1>

          <div className="mb-5 print-break">
            <h3 className="font-serif-title text-lg text-[var(--gold)] pb-1 mb-2" style={{ borderBottom: "1px solid var(--gold)" }}>{t.w1Title}</h3>
            {w1Filled.length === 0 ? <p className="text-sm text-[var(--ink-soft)]">{t.emptyDay}</p> : w1Filled.map(({ i, d }) => (
              <div key={i} className="mb-2">
                <p className="text-sm font-semibold text-[var(--ink)]">{t.dayWord} {i + 1} — {t.energyWord} {d.energie}/10 · {t.chargeWord} {d.charge ?? 5}/10</p>
                <p className="text-sm">✓ {join(selectedLabels(d.positif, t.w1Positif, d.autrePositif))} · ✕ {join(selectedLabels(d.negatif, t.w1Negatif, d.autreNegatif))} · ★ {join(selectedLabels(d.victoire, t.w1Victoire, d.autreVictoire))}</p>
                {d.note && <p className="text-sm text-[var(--ink-soft)]">{t.noteWord} : {d.note}</p>}
              </div>
            ))}
            {(state.w1.bilan.patterns || state.w1.bilan.ajust.some((a) => a)) && (
              <div className="mt-2">
                <p className="text-sm font-semibold text-[var(--gold)]">{t.bilan1Title}</p>
                {state.w1.bilan.patterns && <p className="text-sm"><strong>{t.bilan1PatternsLabel} :</strong> {state.w1.bilan.patterns}</p>}
                {state.w1.bilan.ajust.some((a) => a) && <p className="text-sm"><strong>{t.bilan1AjustLabel} :</strong> {state.w1.bilan.ajust.filter((a) => a).join(" · ")}</p>}
              </div>
            )}
            {w1Filled.length > 0 && (
              <div className="mt-3" data-testid="prog-print-energychart">
                <p className="text-sm font-semibold text-[var(--ink)] mb-1">{t.energyChartTitle}</p>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={energyData} margin={{ top: 5, right: 12, left: -18, bottom: 0 }}>
                    <CartesianGrid stroke="#eee3d6" strokeDasharray="3 3" />
                    <XAxis dataKey="jour" tick={{ fill: "#6E5C53", fontSize: 11 }} />
                    <YAxis domain={[0, 10]} ticks={[0, 2, 4, 6, 8, 10]} tick={{ fill: "#a1907f", fontSize: 10 }} />
                    <Tooltip formatter={(v) => `${v}/10`} />
                    <Legend />
                    <Line type="monotone" dataKey="energie" name={t.energyWord} stroke="#B58C67" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
                    <Line type="monotone" dataKey="charge" name={t.chargeWord} stroke="#8A9A6D" strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
                  </LineChart>
                </ResponsiveContainer>
                <p className="text-sm mt-1" data-testid="prog-print-averages"><strong>{t.avgWord} :</strong> {t.energyWord} {avgE}/10 · {t.chargeWord} {avgC}/10</p>
              </div>
            )}
          </div>

          <div className="mb-5 print-break">
            <h3 className="font-serif-title text-lg text-[var(--gold)] pb-1 mb-2" style={{ borderBottom: "1px solid var(--gold)" }}>{t.w2Title}</h3>
            {w2Filled.length === 0 ? <p className="text-sm text-[var(--ink-soft)]">{t.emptyDay}</p> : w2Filled.map(({ i, d }) => (
              <div key={i} className="mb-2">
                <p className="text-sm font-semibold text-[var(--ink)]">{t.dayWord} {i + 1}</p>
                <p className="text-sm"><strong>{t.w2FacilitantTitle} :</strong> {join(selectedLabels(d.facilitant, t.w2Facilitant, d.autreF))}</p>
                <p className="text-sm"><strong>{t.w2EmpechantTitle} :</strong> {join(selectedLabels(d.empechant, t.w2Empechant, d.autreE))}</p>
                <p className="text-sm"><strong>{t.w2BloquantTitle} :</strong> {join(selectedLabels(d.bloquant, t.w2Bloquant, d.autreB))}</p>
                {d.note && <p className="text-sm text-[var(--ink-soft)]">{t.noteWord} : {d.note}</p>}
              </div>
            ))}
            {w2Filled.length > 0 && (
              <div className="mt-2" data-testid="prog-print-w2freq">
                <p className="text-sm font-semibold text-[var(--gold)]">{t.w2FreqTitle}</p>
                <p className="text-sm"><strong>{t.w2FacilitantTitle} :</strong> {join(topFactors(state.w2.days, "facilitant", t.w2Facilitant))}</p>
                <p className="text-sm"><strong>{t.w2EmpechantTitle} :</strong> {join(topFactors(state.w2.days, "empechant", t.w2Empechant))}</p>
                <p className="text-sm"><strong>{t.w2BloquantTitle} :</strong> {join(topFactors(state.w2.days, "bloquant", t.w2Bloquant))}</p>
              </div>
            )}
          </div>

          <div className="print-break">
            <h3 className="font-serif-title text-lg text-[var(--gold)] pb-1 mb-2" style={{ borderBottom: "1px solid var(--gold)" }}>{t.w3Title}</h3>
            {state.w3.priorites.some((p) => p) ? (
              <ol className="space-y-1">{state.w3.priorites.map((p, i) => (p ? (<li key={i} className="text-sm"><strong>{t.prioLevelsShort[i]} :</strong> {p}</li>) : null))}</ol>
            ) : <p className="text-sm text-[var(--ink-soft)]">{t.emptyDay}</p>}
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-3 mt-8 no-print">
          <button onClick={doExport} disabled={exporting} data-testid="prog-export" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all disabled:opacity-60"><Printer size={16} /> {exporting ? t.exporting : t.exportPdf}</button>
          <button onClick={restart} data-testid="prog-restart" className="px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-all">{t.restart}</button>
          {!patientMode && <Link to="/" className="px-6 py-3 rounded-full border border-[var(--border)] text-[var(--ink-soft)] font-medium hover:border-[var(--gold)] hover:text-[var(--gold)] transition-all">{t.allModules}</Link>}
        </div>
        <p className="text-center text-xs text-[var(--ink-soft)] max-w-md mx-auto mt-4 no-print" data-testid="prog-depot-hint">{t.depotHint}</p>
      </div>
    </Layout>
  );
}
